import HttpService from "../services/HttpService";
import { useQuery } from "@tanstack/react-query";
import { Box, Grid } from "@mui/material";
import Payments from "../components/Payments";
import CardStatsElement from "../components/CardStatsElement";
import UserService from "../services/UserService";
import { useState } from "react";
import dayjs from "dayjs";
import { formatPayfileDateRange } from "../util/payfileDateRange";

export default function QueryPayments() {
  const [page, setPage] = useState(0);
  const [filterString, setFilterString] = useState("");
  const [dateFromValue, setDateFromValue] = useState(
    dayjs().subtract(7, "day").startOf("day")
  );
  const [dateToValue, setDateToValue] = useState(dayjs().endOf("day"));
  const [statusFilter, setStatusFilter] = useState([]);
  const [employerFilter, setEmployerFilter] = useState(null);
  const [subDepartmentFilter, setSubDepartmentFilter] = useState(null);

  async function fetchPayments(
    page,
    filterString,
    dateFromValue,
    dateToValue,
    statusFilter,
    employerFilter,
    subDepartmentFilter
  ) {
    let url = window.API_URL + `/payfile/pending?pageSize=10&page=` + page;
    const { from, to } = formatPayfileDateRange(dateFromValue, dateToValue);
    if (filterString) url += `&filter=` + filterString;
    if (from) url += `&from=` + from;
    if (to) url += `&to=` + to;
    if (statusFilter && statusFilter.length > 0)
      url += `&status=` + statusFilter.join(",");
    if (employerFilter) url += `&employerId=` + employerFilter;
    if (subDepartmentFilter) url += `&subDepartmentId=` + subDepartmentFilter;

    const response = await HttpService.getAxiosClient().get(url);
    return response;
  }

  async function fetchPaymentsCount(
    filterString,
    dateFromValue,
    dateToValue,
    statusFilter,
    employerFilter,
    subDepartmentFilter
  ) {
    let url = window.API_URL + `/payfile/pending/count?`;
    let params = [];
    const { from, to } = formatPayfileDateRange(dateFromValue, dateToValue);
    if (filterString) params.push(`filter=` + filterString);
    if (from) params.push(`from=` + from);
    if (to) params.push(`to=` + to);
    if (statusFilter && statusFilter.length > 0)
      params.push(`status=` + statusFilter.join(","));
    if (employerFilter) params.push(`employerId=` + employerFilter);
    if (subDepartmentFilter)
      params.push(`subDepartmentId=` + subDepartmentFilter);
    if (params.length > 0) url += params.join("&");

    const response = await HttpService.getAxiosClient().get(url);
    return response;
  }

  async function fetchPayfileStats() {
    const response = await HttpService.getAxiosClient().get(
      window.API_URL + `/payfile/stats`
    );
    return response;
  }

  async function fetchLocations() {
    if (UserService.hasRole(["pc-query-locations"])) {
      const response = await HttpService.getAxiosClient().get(
        window.API_URL + `/locations`
      );
      return response;
    }
    return [];
  }

  const {
    data: dataStats,
    isLoading: isLoadingStats,
    isError: isErrorStats,
    refetch: refetchStats,
  } = useQuery(["payfileStats"], fetchPayfileStats);

  const { data, isFetching, isLoading, isError, refetch } = useQuery(
    [
      "payments",
      page,
      filterString,
      dateFromValue,
      dateToValue,
      statusFilter,
      employerFilter,
      subDepartmentFilter,
    ],
    () =>
      fetchPayments(
        page,
        filterString,
        dateFromValue,
        dateToValue,
        statusFilter,
        employerFilter,
        subDepartmentFilter
      ),
    {
      keepPreviousData: true,
    }
  );

  const {
    data: dataCount,
    isFetching: isFetchingDataCount,
    isLoading: isLoadingDataCount,
    isError: isErrorDataCount,
    refetch: refetchDataCount,
  } = useQuery(
    [
      "paymentsCount",
      filterString,
      dateFromValue,
      dateToValue,
      statusFilter,
      employerFilter,
      subDepartmentFilter,
    ],
    () =>
      fetchPaymentsCount(
        filterString,
        dateFromValue,
        dateToValue,
        statusFilter,
        employerFilter,
        subDepartmentFilter
      ),
    {
      keepPreviousData: true,
    }
  );

  const {
    data: dataLocations,
    isLoading: isLoadingLocations,
    isError: isErrorLocations,
  } = useQuery(["locations"], fetchLocations);

  return (
    <Box sx={{ margin: "0", padding: "0 150px" }}>
      <br />
      <Grid container spacing={2}>
        <CardStatsElement
          isErrorStats={isErrorStats}
          isLoadingStats={isLoadingStats}
          text="Payments received today"
          data={dataStats && dataStats.data.receivedToday}
          type="amount"
        />
        <CardStatsElement
          isErrorStats={isErrorStats}
          isLoadingStats={isLoadingStats}
          text="Payments loaded today"
          data={dataStats && dataStats.data.loadedToday}
          type="amount"
        />
        <CardStatsElement
          isErrorStats={isErrorStats}
          isLoadingStats={isLoadingStats}
          text="Payments pending today"
          data={dataStats && dataStats.data.pendingToday}
          type="amount"
        />
        <CardStatsElement
          isErrorStats={isErrorStats}
          isLoadingStats={isLoadingStats}
          text="Total pending payments"
          data={dataStats && dataStats.data.pendingTotal}
          type="amount"
        />
      </Grid>
      <Payments
        isFetching={isFetching}
        isLoading={isLoading}
        isError={isError}
        data={data}
        dataStats={dataStats}
        dataLocations={dataLocations}
        isLoadingLocations={isLoadingLocations}
        isErrorLocations={isErrorLocations}
        refetchFunction={refetch}
        refetchStatsFunction={refetchStats}
        setPageFunction={setPage}
        setFilterStringFunction={setFilterString}
        dataCount={dataCount}
        isFetchingDataCount={isFetchingDataCount}
        isLoadingDataCount={isLoadingDataCount}
        isErrorDataCount={isErrorDataCount}
        refetchDataCount={refetchDataCount}
        dateFromValue={dateFromValue}
        dateToValue={dateToValue}
        setDateFromValue={setDateFromValue}
        setDateToValue={setDateToValue}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        setEmployerFilter={setEmployerFilter}
        setSubDepartmentFilter={setSubDepartmentFilter}
        title="Pending Payments"
        type="pending"
      />
    </Box>
  );
}
